import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Fade,
  Zoom
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Analytics as AnalyticsIcon,
  Psychology as AIIcon,
  CompareArrows as MappingIcon,
  Assessment as CalculatorIcon,
  TrendingUp as OptimizationIcon,
  CheckCircle as CheckIcon,
  Schedule as ClockIcon,
  SmartToy as RobotIcon
} from '@mui/icons-material';

interface AnalysisStage {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  duration: number; // in seconds
  details: string[];
  completed: boolean;
}

const DetailedLoadingProgress: React.FC = () => {
  const [currentStage, setCurrentStage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [stageProgress, setStageProgress] = useState(0);
  const [currentDetail, setCurrentDetail] = useState(0);

  const analysisStages: AnalysisStage[] = [
    {
      id: 'parsing',
      title: 'Parsing Your AWS Bill',
      description: 'Extracting and categorizing services, costs, and usage patterns',
      icon: <UploadIcon />,
      duration: 15,
      details: [
        'Reading bill structure and metadata',
        'Extracting service line items',
        'Parsing usage quantities and costs',
        'Identifying regions and availability zones',
        'Categorizing service types'
      ],
      completed: false
    },
    {
      id: 'analysis',
      title: 'Deep Service Analysis',
      description: 'Analyzing each AWS service and identifying optimization opportunities',
      icon: <AnalyticsIcon />,
      duration: 25,
      details: [
        'Analyzing compute instance usage patterns',
        'Examining storage utilization and tiers',
        'Reviewing database configurations',
        'Identifying underutilized resources',
        'Calculating cost per service category',
        'Detecting potential savings opportunities'
      ],
      completed: false
    },
    {
      id: 'ai_insights',
      title: 'AI-Powered Insights',
      description: 'Leveraging Claude AI to generate intelligent migration recommendations',
      icon: <AIIcon />,
      duration: 30,
      details: [
        'Generating executive summary insights',
        'Identifying key pain points and challenges',
        'Creating personalized recommendations',
        'Analyzing workload characteristics',
        'Developing migration strategy options',
        'Assessing business impact and priorities'
      ],
      completed: false
    },
    {
      id: 'mapping',
      title: 'OCI Service Mapping',
      description: 'Finding optimal Oracle Cloud Infrastructure equivalents',
      icon: <MappingIcon />,
      duration: 20,
      details: [
        'Mapping AWS services to OCI equivalents',
        'Matching compute shapes and configurations',
        'Identifying compatible storage options',
        'Finding database migration paths',
        'Comparing networking capabilities',
        'Validating feature compatibility'
      ],
      completed: false
    },
    {
      id: 'calculation',
      title: 'Cost Calculation & Optimization',
      description: 'Computing OCI costs and identifying maximum savings potential',
      icon: <CalculatorIcon />,
      duration: 15,
      details: [
        'Calculating OCI equivalent pricing',
        'Applying volume discounts and optimizations',
        'Computing potential cost savings',
        'Analyzing ROI and payback periods',
        'Generating cost comparison reports'
      ],
      completed: false
    },
    {
      id: 'finalization',
      title: 'Finalizing Analysis',
      description: 'Preparing comprehensive migration recommendations and sales materials',
      icon: <OptimizationIcon />,
      duration: 10,
      details: [
        'Generating executive dashboard',
        'Creating detailed comparison charts',
        'Preparing migration roadmap',
        'Building sales pitch materials',
        'Finalizing recommendations'
      ],
      completed: false
    }
  ];

  const [stages, setStages] = useState(analysisStages);

  useEffect(() => {
    let totalDuration = 0;
    let currentStageDuration = 0;
    let stageIndex = 0;

    // Calculate which stage we should be in based on elapsed time
    for (let i = 0; i < stages.length; i++) {
      if (totalDuration + stages[i].duration > (progress / 100) * 115) { // 115 seconds total
        stageIndex = i;
        currentStageDuration = (progress / 100) * 115 - totalDuration;
        break;
      }
      totalDuration += stages[i].duration;
    }

    setCurrentStage(stageIndex);
    setStageProgress((currentStageDuration / stages[stageIndex]?.duration || 1) * 100);

    // Update detail index based on stage progress
    const detailCount = stages[stageIndex]?.details.length || 1;
    const detailIndex = Math.floor((stageProgress / 100) * detailCount);
    setCurrentDetail(Math.min(detailIndex, detailCount - 1));

    // Mark completed stages
    setStages(prevStages => 
      prevStages.map((stage, index) => ({
        ...stage,
        completed: index < stageIndex || (index === stageIndex && stageProgress >= 100)
      }))
    );
  }, [progress]);

  // Auto-increment progress
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          return 100;
        }
        // Vary the increment speed to make it feel more realistic
        const increment = Math.random() * 1.5 + 0.5; // 0.5 to 2.0
        return Math.min(prev + increment, 100);
      });
    }, 800); // Update every 800ms

    return () => clearInterval(timer);
  }, []);

  const totalElapsedTime = Math.floor((progress / 100) * 115); // 115 seconds total
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Card sx={{ 
      p: 4, 
      maxWidth: 900, 
      mx: 'auto',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      border: '1px solid #dee2e6'
    }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 2,
          mb: 2
        }}>
          <Zoom in={true}>
            <Box sx={{ 
              p: 2, 
              borderRadius: '50%', 
              bgcolor: 'primary.main',
              color: 'white',
              animation: 'pulse 2s infinite'
            }}>
              <RobotIcon sx={{ fontSize: 32 }} />
            </Box>
          </Zoom>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Alfred is Analyzing Your Bill
          </Typography>
        </Box>
        
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          Deep analysis in progress - finding optimal OCI migration strategies
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, mb: 3 }}>
          <Chip 
            icon={<ClockIcon />}
            label={`Elapsed: ${formatTime(totalElapsedTime)}`}
            color="primary"
            variant="outlined"
          />
          <Chip 
            label={`${progress.toFixed(1)}% Complete`}
            color="success"
            variant="filled"
          />
        </Box>

        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: 'linear-gradient(90deg, #2196f3 0%, #21cbf3 100%)'
            }
          }}
        />
      </Box>

      {/* Current Stage Details */}
      <Fade in={true} key={currentStage}>
        <Card sx={{ 
          p: 3, 
          mb: 3, 
          border: '2px solid',
          borderColor: 'primary.main',
          bgcolor: 'primary.50',
          animation: 'glow 3s ease-in-out infinite',
          transition: 'all 0.3s ease-in-out'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: '50%', 
              bgcolor: 'primary.main',
              color: 'white'
            }}>
              {stages[currentStage]?.icon}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {stages[currentStage]?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stages[currentStage]?.description}
              </Typography>
            </Box>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={stageProgress} 
            sx={{ 
              mb: 2,
              height: 6,
              borderRadius: 3,
              bgcolor: 'rgba(33, 150, 243, 0.2)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                bgcolor: 'primary.main'
              }
            }}
          />
          
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 500, 
              color: 'primary.dark',
              minHeight: 24
            }}
          >
            ▶ {stages[currentStage]?.details[currentDetail]}
          </Typography>
        </Card>
      </Fade>

      {/* Progress Stepper */}
      <Stepper activeStep={currentStage} orientation="vertical">
        {stages.map((stage, index) => (
          <Step key={stage.id} completed={stage.completed}>
            <StepLabel 
              icon={stage.completed ? <CheckIcon color="success" /> : stage.icon}
              sx={{
                '& .MuiStepLabel-label': {
                  fontWeight: index === currentStage ? 700 : 400,
                  color: stage.completed ? 'success.main' : 
                         index === currentStage ? 'primary.main' : 'text.secondary'
                }
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 'inherit' }}>
                {stage.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stage.description}
              </Typography>
            </StepLabel>
            
            {index === currentStage && (
              <StepContent>
                <List dense>
                  {stage.details.map((detail, detailIndex) => (
                    <ListItem key={detailIndex} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {stage.completed || detailIndex <= currentDetail ? (
                          <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        ) : (
                          <Box sx={{ 
                            width: 6, 
                            height: 6, 
                            borderRadius: '50%', 
                            bgcolor: 'grey.400' 
                          }} />
                        )}
                      </ListItemIcon>
                      <ListItemText 
                        primary={detail}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                            fontWeight: detailIndex === currentDetail && !stage.completed ? 600 : 400,
                            color: stage.completed || detailIndex <= currentDetail ? 
                                   'success.dark' : 'text.secondary'
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </StepContent>
            )}
          </Step>
        ))}
      </Stepper>

      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          
          @keyframes slideIn {
            from { transform: translateX(-100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          
          @keyframes glow {
            0% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.3); }
            50% { box-shadow: 0 0 20px rgba(33, 150, 243, 0.6), 0 0 30px rgba(33, 150, 243, 0.4); }
            100% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.3); }
          }
        `}
      </style>
    </Card>
  );
};

export default DetailedLoadingProgress;